import React from "react";
import { Image, View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import {
  MessageSquare,
  Clock,
  FileText,
  Sparkles,
  Plus,
} from "lucide-react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import {
  QuestionLayout,
  IconInput,
} from "@src/packages/medical-history/components";
import {
  CardSurface,
  DocumentRow,
  DateField,
  MoonSelectField,
} from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import { useDietSupplements } from "../DietSupplementsContext";
import type { SupplementDetail } from "../DietSupplementsContext";
import type { DietSupplementsStackParamList } from "../DietSupplementsNavigator";
import {
  getSupplementLabelKey,
  SUPPLEMENT_OPTIONS,
  FREQUENCY_OPTIONS,
} from "../options";
import type { FrequencyType } from "../options";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";

const supplementIllustration =
  require("@src/apps/moon/assets/images/pill-capsule.png") as number;

function FrequencyPicker({
  value,
  onChange,
}: {
  value: FrequencyType | null | undefined;
  onChange: (freq: FrequencyType) => void;
}) {
  const t = useTranslations();

  return (
    <MoonSelectField
      value={value ?? null}
      onChange={onChange}
      options={FREQUENCY_OPTIONS.map((option) => ({
        label: t(option.labelKey),
        value: option.id,
      }))}
      placeholder={t(
        "med_history.lifestyle.diet_supplements.frequency_placeholder",
      )}
      pickerTitle={t(
        "med_history.lifestyle.diet_supplements.frequency_modal_title",
      )}
      startVisual={<Clock size={16} color={EMBER.colors.textFaint} />}
      action="supplement_frequency"
      style={{ width: "100%" }}
    />
  );
}

export function SupplementsDetailsScreen() {
  const t = useTranslations();
  const { state, updateState } = useDietSupplements();
  const navigation =
    useNavigation<NativeStackNavigationProp<DietSupplementsStackParamList>>();
  const theme = useMedicalHistoryTheme();

  const updateSupplement = (
    index: number,
    updates: Partial<SupplementDetail>,
  ) => {
    const updated = state.supplements.map((s, i) =>
      i === index ? { ...s, ...updates } : s,
    );
    updateState({ supplements: updated });
  };

  const addSupplement = (supplement: SupplementDetail["supplement"]) => {
    updateState({
      supplements: [...state.supplements, { supplement }],
    });
  };

  const handleSave = () => {
    navigation.navigate("DietSupplementsComplete");
  };

  return (
    <QuestionLayout
      title={t("med_history.lifestyle.diet_supplements.details_title")}
      subtitle={t("med_history.lifestyle.diet_supplements.details_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={false}
      saveLabel={t("med_history.lifestyle.next")}
    >
      <View style={styles.container}>
        {state.supplements.map((entry, index) => {
          const isHerbal = entry.supplement === "herbal";
          const isOther = entry.supplement === "other";
          const label = t(getSupplementLabelKey(entry.supplement));
          const SupplementIcon =
            SUPPLEMENT_OPTIONS.find((o) => o.id === entry.supplement)?.icon ??
            Sparkles;

          return (
            <CardSurface key={index} style={styles.detailCard}>
              <View style={styles.cardHeader}>
                <View style={styles.cardTitleRow}>
                  <SupplementIcon size={14} color={theme.accentLight} />
                  <Text style={[styles.cardTitle, { color: theme.text }]}>
                    {label}
                  </Text>
                </View>
                <Text style={[styles.cardSubtitle, { color: theme.textFaint }]}>
                  {t(
                    "med_history.lifestyle.diet_supplements.details_card_subtitle",
                  )}
                </Text>
              </View>

              {(isHerbal || isOther) && (
                <IconInput
                  icon={FileText}
                  label={t(
                    isHerbal
                      ? "med_history.lifestyle.diet_supplements.herbal_label"
                      : "med_history.lifestyle.diet_supplements.other_supplement_label",
                  )}
                  placeholder={
                    isHerbal
                      ? t(
                          "med_history.lifestyle.diet_supplements.herbal_placeholder",
                        )
                      : t(
                          "med_history.lifestyle.diet_supplements.other_supplement_placeholder",
                        )
                  }
                  value={entry.other ?? ""}
                  onChangeText={(text) =>
                    updateSupplement(index, { other: text || null })
                  }
                />
              )}

              <DateField
                label={t(
                  "med_history.lifestyle.diet_supplements.start_date_label",
                )}
                value={entry.startDate ?? ""}
                onChange={(iso) =>
                  updateSupplement(index, { startDate: iso || null })
                }
              />

              <FrequencyPicker
                value={entry.frequency}
                onChange={(freq) =>
                  updateSupplement(index, { frequency: freq })
                }
              />

              <IconInput
                icon={MessageSquare}
                label={t("med_history.lifestyle.diet_supplements.notes_label")}
                placeholder={t(
                  "med_history.lifestyle.diet_supplements.notes_placeholder",
                )}
                value={entry.notes ?? ""}
                onChangeText={(text) =>
                  updateSupplement(index, { notes: text || null })
                }
                multiline
                numberOfLines={3}
              />
            </CardSurface>
          );
        })}

        <DocumentRow
          onPress={() => {
            // Add another of the first supplement type as default
            const firstType = state.supplements[0]?.supplement ?? "folic_acid";
            addSupplement(firstType);
          }}
          accessibilityLabel={t(
            "med_history.lifestyle.diet_supplements.add_another",
          )}
          leading={
            <Image
              source={supplementIllustration}
              resizeMode="contain"
              style={styles.addIllustration}
            />
          }
          title={t("med_history.lifestyle.diet_supplements.add_another")}
          trailing={
            <Plus size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
          }
          showChevron={false}
          style={styles.addRow}
        />
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
    gap: 16,
  },
  detailCard: {
    padding: 16,
    gap: 16,
  },
  cardHeader: {
    gap: 4,
  },
  cardTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: "700",
  },
  cardSubtitle: {
    fontSize: 12,
  },
  addRow: {
    width: "100%",
  },
  addIllustration: {
    width: 34,
    height: 34,
  },
});
